// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ColumnAlignment, ColumnAlignmentDefinition } from "@insite/client-framework/Types/FieldDefinition";
import Select from "@insite/mobius/Select";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip/Tooltip";
import { CustomEditorTemplateProps } from "@steiner/App/CustomField/CustomFieldDefinition";
import * as React from "react";
import styled, { css } from "styled-components";

type Props = CustomEditorTemplateProps<ColumnAlignment[], ColumnAlignmentDefinition>;

const StandardControl: React.FC<any> = props => (
    <>
        <Label htmlFor={props.fieldDefinition.name} id={props.labelId}>
            {props.fieldDefinition.displayName}
            {props.fieldDefinition.isRequired}
            {props.fieldDefinition.tooltip &&
                (props.fieldDefinition.tooltipPosition === "left" ? (
                    <Tooltip {...leftTooltipStyles} text={props.fieldDefinition.tooltip} />
                ) : (
                    <Tooltip {...tooltipStyles} text={props.fieldDefinition.tooltip} />
                ))}
            {props.fieldDefinition.fieldType && (
                <FieldTypeStyle>{props.fieldDefinition.fieldType.substr(0, 1)}</FieldTypeStyle>
            )}
        </Label>
        <div data-test-selector={`controlFor_${props.fieldDefinition.name}`}>{props.children}</div>
    </>
);

const alignmentOptions = [
    { value: "topLeft", label: "Top Left" },
    { value: "topCenter", label: "Top Center" },
    { value: "topRight", label: "Top Right" },
    { value: "middleLeft", label: "Center Left" },
    { value: "middleCenter", label: "Center" },
    { value: "middleRight", label: "Center Right" },
    { value: "bottomLeft", label: "Bottom Left" },
    { value: "bottomCenter", label: "Bottom Center" },
    { value: "bottomRight", label: "Bottom Right" },
];

export default class StrColumnAlignmentField extends React.Component<Props> {
    updateColumn = (index: number, value: ColumnAlignment) => {
        const {
            fieldValue,
            updateField,
            fieldDefinition: { name },
        } = this.props;

        const newFieldValue = { ...fieldValue };
        newFieldValue[index] = value;

        updateField(name, newFieldValue);
    };

    render() {
        const { item, fieldDefinition, fieldValue } = this.props;

        const columns = item.fields["columns"];
        if (!columns || !Array.isArray(columns)) {
            return null;
        }

        if (!fieldValue) {
            this.props.updateField(fieldDefinition.name, []);
            return null;
        }

        return (
            <StandardControl fieldDefinition={fieldDefinition}>
                {columns.map((column, index) => (
                    <ColumnAlignmentSelector
                        key={index}
                        index={index}
                        updateColumn={this.updateColumn}
                        value={fieldValue[index] || "topLeft"}
                    />
                ))}
            </StandardControl>
        );
    }
}

interface SelectorProps {
    value: ColumnAlignment;
    index: number;
    updateColumn: (index: number, value: ColumnAlignment) => void;
}

class ColumnAlignmentSelector extends React.Component<SelectorProps> {
    onChange = (event: React.FormEvent<HTMLSelectElement>) => {
        const value = event.currentTarget.value;
        this.props.updateColumn(this.props.index, value as ColumnAlignment);
    };

    render() {
        const { index, value } = this.props;

        return (
            <Wrapper>
                <span>Column {index + 1}</span>
                <Select onChange={this.onChange} value={value}>
                    {alignmentOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </Select>
            </Wrapper>
        );
    }
}

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 10px;
    > span {
        margin-right: 10px;
        min-width: 80px;
    }
`;

const Label = styled.label`
    display: block;
    font-size: 16px;
    line-height: 21px;
    font-weight: bold;
    margin: 25px 0 -4px;
    text-transform: uppercase;
    position: relative;
`;

const FieldTypeStyle = styled.span`
    position: absolute;
    right: 0;
    font-size: 12px;
    font-weight: normal;
`;

const RequiredStyle = styled.span`
    color: ${props => props.theme.colors.text.accent};
    text-transform: none;
    margin-left: 4px;
`;

const tooltipStyles: TooltipPresentationProps = {
    cssOverrides: {
        tooltipWrapper: css`
            margin-left: 5px;
        `,
    },
};

const leftTooltipStyles: TooltipPresentationProps = {
    cssOverrides: {
        tooltipWrapper: css`
            margin-left: 5px;
        `,
        tooltipContainer: css`
            transform: translateX(-75%);
        `,
        tooltipArrow: css`
            left: 75%;
        `,
    },
};
